// Typed wrappers around the My.Ferry endpoints, scoped to `/ferry/api/v1/*`.
//
// The runtime is the shared @my/platform/http transport, so bearer token,
// X-App-Key and Result<T> error mapping behave identically to every other call
// in the suite. Two idempotency details differ from the other packages:
//   • create takes the caller's X-Idempotency-Key, so a retry after a timeout
//     can resend the SAME key and get the first answer back instead of a second
//     booking at the carrier (the transport only mints a fresh key when none is
//     set);
//   • cancel is a DELETE, which the transport never stamps, so it sets its own.
//
// Types are hand-written against the BE DTOs (same convention as @my/check-api)
// as of the 16.10.26 multi-carrier contract. Enum unions mirror the C# enums by
// NAME, because the BE serialises them as strings. Every DateTime is UTC with a
// trailing `Z`; callers display in local time.
import { request, type ApiInfo } from '@my/platform/http';

const BASE = '/ferry/api/v1';

// ────────────────────────────────────────────────────────────────────────────
// Enums — mirror My.Ferry.Domain.Enums
// ────────────────────────────────────────────────────────────────────────────

export type FerryBaseStatus = 'Active' | 'Inactive';

/** Suite app that created a booking (`X-App-Key` of the caller). */
export type FerryAppName =
  | 'Tms'
  | 'Depot'
  | 'Subsistence'
  | 'Safety'
  | 'Connect'
  | 'Check'
  | 'Gps'
  | 'Ferry'
  | 'Hub';

/** `RouteCategory` — the sea area, one judgement per route. */
export type FerryRouteCategory =
  | 'Unknown'
  | 'IrishSea'
  | 'CelticSea'
  | 'EnglishChannel'
  | 'NorthSea'
  | 'BayOfBiscay'
  | 'BalticSea'
  | 'Other';

export type FerryAccompaniment = 'Unknown' | 'Accompanied' | 'Unaccompanied' | 'Both';

export type FerrySailingStatus = 'Unknown' | 'Open' | 'Departed' | 'Arrived' | 'Cancelled';

/** Height class of a capacity row; above 4 m is High. */
export type FerryHeightClass = 'Unknown' | 'Standard' | 'High';

export type FerryCapacitySource = 'Unknown' | 'Block' | 'Service' | 'Portal';

/**
 * `CarrierBookingStatus` — the carrier's own status. Stena sets the first nine;
 * Rejected..Released are reserved for carriers that report those stages.
 * Final (the daily refresh stops re-reading): Cancelled, Shipped, NoShow,
 * Rejected, Arrived, Discharged, Released.
 */
export type FerryCarrierBookingStatus =
  | 'Unknown'
  | 'Block'
  | 'Cancelled'
  | 'CheckedIn'
  | 'CheckedInWaiter'
  | 'Confirmed'
  | 'NoShow'
  | 'Shipped'
  | 'Waitlisted'
  | 'Rejected'
  | 'CustomsCleared'
  | 'Loaded'
  | 'Arrived'
  | 'Discharged'
  | 'Released';

/**
 * `IntegrationStatus` — OUR call to the carrier. Failed means the carrier
 * refused or was unreachable; `message` on the booking says why.
 */
export type FerryIntegrationStatus = 'Pending' | 'Requesting' | 'Completed' | 'Failed';

export type FerryIntegrationAction = 'None' | 'Create' | 'Amend' | 'Cancel';

/** `UkCustomsProcedure`, on Stena's numbering. */
export type FerryUkCustomsProcedure = 'Unknown' | 'C' | 'EX' | 'N' | 'PL' | 'T1' | 'TX';

export type FerryBookingEventKind =
  | 'Create'
  | 'Amend'
  | 'Cancel'
  | 'Refresh'
  | 'StatusChange'
  | 'PortEvent';

// ────────────────────────────────────────────────────────────────────────────
// Reference data
// ────────────────────────────────────────────────────────────────────────────

export interface FerryCarrier {
  id: string;
  /** Carrier code, e.g. `STENA`. */
  code: string;
  name: string;
  status: FerryBaseStatus;
}

/** One directional route at a carrier. Codes are the carrier's own (`BECN`); port codes are UN/LOCODEs. */
export interface FerryRoute {
  id: string;
  carrierId: string;
  code: string;
  description?: string | null;
  category: FerryRouteCategory;
  originPortCode?: string | null;
  originPortName?: string | null;
  destinationPortCode?: string | null;
  destinationPortName?: string | null;
  originCountryIsoCode?: string | null;
  destinationCountryIsoCode?: string | null;
  /** `XI` for a Northern Ireland port; otherwise the country's own territory. */
  originCustomsTerritory?: string | null;
  destinationCustomsTerritory?: string | null;
  /** IANA zone of each port (`Europe/Dublin`), for showing sailing times in port time; null when unknown. */
  originTimeZoneId?: string | null;
  destinationTimeZoneId?: string | null;
  accompaniment: FerryAccompaniment;
  typicalDurationMinutes?: number | null;
  typicalCheckInMinutes?: number | null;
  typicalDriveOffMinutes?: number | null;
  /** Entered by hand in Ferry rather than synced from the carrier; the sync leaves it alone. */
  isManual: boolean;
  isActive: boolean;
  lastSeenUtc?: string | null;
}

/** A carrier's vehicle type. `accompanied: false` is a unit without a driver (trailer); `true` needs a vehicle registration. */
export interface FerryVehicleType {
  id: string;
  carrierId: string;
  code: string;
  customerCode?: string | null;
  description?: string | null;
  /** Grouping some carriers publish; null for Stena. */
  category?: string | null;
  defaultWeight?: number | null;
  defaultLength?: number | null;
  defaultWidth?: number | null;
  defaultHeight?: number | null;
  accompanied: boolean;
  isActive: boolean;
  lastSeenUtc?: string | null;
}

export interface FerrySailing {
  id: string;
  carrierId: string;
  routeCode: string;
  /** UTC. A booking's `scheduledDeparture` must equal one of these. */
  scheduledDeparture: string;
  scheduledArrival?: string | null;
  shipName?: string | null;
  imoNumber?: number | null;
  status: FerrySailingStatus;
  lastSyncedUtc: string;
}

// ────────────────────────────────────────────────────────────────────────────
// Capacity
// ────────────────────────────────────────────────────────────────────────────

/** Free capacity in one class on a sailing. A class with nothing free is simply absent. */
export interface FerryCapacityFree {
  /** A height class name for Stena (`Standard`, `High`, rarely `Unknown`); a carrier's own class elsewhere. */
  class: string;
  count: number;
}

export interface FerryCapacitySailing {
  routeCode: string;
  scheduledDeparture: string;
  free: FerryCapacityFree[];
  totalRows: number;
}

/** One of the organisation's rows at the carrier (a block or a booking) on a sailing. */
export interface FerryCapacitySlot {
  id: string;
  organisationId: string;
  carrierId: string;
  credentialId: string;
  routeCode: string;
  scheduledDeparture: string;
  carrierBookingNumber: number;
  liveStatus: FerryCarrierBookingStatus;
  initialStatus: FerryCarrierBookingStatus;
  heightClass: FerryHeightClass;
  vehicleHeight?: number | null;
  source: FerryCapacitySource;
  /** Set once the row became one of our bookings. */
  carrierBookingId?: string | null;
  lastSyncedUtc: string;
}

export interface FerryCapacity {
  sailings: FerryCapacitySailing[];
  slots: FerryCapacitySlot[];
}

/** Free blocks of one class on a sailing summary, 0 when the class is absent. */
export function ferryFreeBlocks(
  sailing: Pick<FerryCapacitySailing, 'free'> | null | undefined,
  cls: string,
): number {
  return sailing?.free?.find((f) => f.class === cls)?.count ?? 0;
}

// ────────────────────────────────────────────────────────────────────────────
// Credentials (read-only here — the secret is never returned)
// ────────────────────────────────────────────────────────────────────────────

export interface FerryCredential {
  id: string;
  organisationId: string;
  carrierId: string;
  /** The carrier's code (STENA), so a credential matches a catalogue entry without the carriers list. */
  carrierCode?: string | null;
  /** The organisation's account at the carrier: Stena's customer number as digits. */
  customerCode: string;
  clientId?: string | null;
  resource?: string | null;
  secretExpiresUtc?: string | null;
  status: FerryBaseStatus;
}

/** POST `/carriercredentials` body: a new account at a carrier. The secret is stored encrypted and never returned. */
export interface CreateFerryCredentialRequest {
  organisationId: string;
  carrierId: string;
  customerCode: string;
  clientId: string;
  clientSecret: string;
  resource?: string | null;
  secretExpiresUtc?: string | null;
}

/** POST `/carriercredentials/{id}/test`: whether the carrier issued a token for the account. */
export interface FerryConnectionTest {
  succeeded: boolean;
  tokenExpiresUtc?: string | null;
  message?: string | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Bookings
// ────────────────────────────────────────────────────────────────────────────

export interface FerryBooking {
  id: string;
  organisationId: string;
  sourceApp: FerryAppName;
  /** The caller's own id for the booking, unique per organisation. */
  externalReference: string;
  carrierId: string;
  credentialId: string;
  routeId: string;
  customerReference: string;
  /** The carrier's number; null until the carrier accepts. */
  carrierBookingNumber?: number | null;
  /** UTC. Null only for carriers that book without a sailing; never for Stena. */
  scheduledDeparture: string | null;
  scheduledArrival?: string | null;
  estimatedArrival?: string | null;
  realDeparture?: string | null;
  realArrival?: string | null;
  shipName?: string | null;
  vehicleTypeCode: string;
  vehicleRegistration?: string | null;
  trailerRegistration?: string | null;
  lengthMetres: number;
  heightMetres: number;
  widthMetres: number;
  /** Cargo only, as the caller sent it. */
  cargoWeightKg: number;
  /** Type default plus cargo, as sent to the carrier. */
  vehicleWeightKg: number;
  numberOfDrivers: number;
  isDangerousGoods: boolean;
  livestock: boolean;
  numberOfPluginCables?: number | null;
  pluginTemperature?: string | null;
  ukCustomsProcedure: FerryUkCustomsProcedure;
  exportId?: string | null;
  importId?: string | null;
  isTransit?: boolean | null;
  driverFirstName?: string | null;
  driverSurname?: string | null;
  driverDateOfBirth?: string | null;
  carrierStatus: FerryCarrierBookingStatus;
  integrationStatus: FerryIntegrationStatus;
  lastAction: FerryIntegrationAction;
  keepCapacity: boolean;
  /** The carrier's reason when `integrationStatus` is Failed. */
  message?: string | null;
  sentUtc?: string | null;
  confirmedUtc?: string | null;
  cancelledUtc?: string | null;
  createdUtc: string;
  updatedUtc?: string | null;
}

/** One call to the carrier about a booking, refused ones included. */
export interface FerryBookingEvent {
  id: string;
  bookingId: string;
  kind: FerryBookingEventKind;
  occurredUtc: string;
  by: string;
  carrierStatusAfter?: FerryCarrierBookingStatus | null;
  code?: string | null;
  description?: string | null;
  succeeded: boolean;
}

/**
 * POST `/ferry/api/v1/bookings` body. Dimensions left null take the vehicle
 * type's defaults; `cargoWeightKg` is cargo only (the type's weight is added
 * by the service). Stena needs `scheduledDeparture` and a `customerReference`
 * of at most 17 characters. The driver/transit fields are for other carriers.
 */
export interface CreateFerryBookingRequest {
  organisationId: string;
  carrierId: string;
  /** Required only when the organisation holds more than one credential at the carrier. */
  credentialId?: string | null;
  externalReference: string;
  routeCode: string;
  scheduledDeparture: string | null;
  customerReference: string;
  vehicleTypeCode: string;
  vehicleRegistration?: string | null;
  trailerRegistration?: string | null;
  lengthMetres?: number | null;
  heightMetres?: number | null;
  widthMetres?: number | null;
  cargoWeightKg: number;
  numberOfDrivers: number;
  isDangerousGoods: boolean;
  livestock: boolean;
  numberOfPluginCables?: number | null;
  pluginTemperature?: string | null;
  ukCustomsProcedure: FerryUkCustomsProcedure;
  exportId?: string | null;
  importId?: string | null;
  isTransit?: boolean | null;
  driverFirstName?: string | null;
  driverSurname?: string | null;
  /** Date only; a time part is dropped by the service. */
  driverDateOfBirth?: string | null;
  keepCapacity: boolean;
}

/** PUT `/ferry/api/v1/bookings/{id}` body: the whole booking again. Carrier, account and external reference are fixed. */
export type AmendFerryBookingRequest = Omit<
  CreateFerryBookingRequest,
  'carrierId' | 'credentialId' | 'externalReference'
>;

export interface FerryBookingListFilter {
  carrierId?: string;
  sourceApp?: FerryAppName;
  externalReference?: string;
  /** Both filter on `scheduledDeparture`, UTC. */
  from?: string;
  to?: string;
  /** Rows created or changed at or after this moment — how a consumer picks up the nightly refresh. */
  updatedSince?: string;
  carrierStatus?: FerryCarrierBookingStatus;
  integrationStatus?: FerryIntegrationStatus;
}

export interface FerryRouteFilter {
  category?: FerryRouteCategory;
  isActive?: boolean;
}

// ────────────────────────────────────────────────────────────────────────────
// Catalogue upkeep (Manager role): carriers and routes Ferry does not sync
// ────────────────────────────────────────────────────────────────────────────

/** POST `/ferry/api/v1/carriers`. */
export interface CreateFerryCarrierRequest {
  code: string;
  name: string;
}

/** PUT `/ferry/api/v1/carriers`. */
export interface UpdateFerryCarrierRequest {
  id: string;
  name: string;
  status: FerryBaseStatus;
}

/** POST `/ferry/api/v1/carrierroutes`: a manual route. Port codes are UN/LOCODEs. */
export interface CreateFerryRouteRequest {
  carrierId: string;
  code: string;
  description?: string | null;
  originPortCode: string;
  originPortName?: string | null;
  destinationPortCode: string;
  destinationPortName?: string | null;
  accompaniment?: FerryAccompaniment;
  typicalDurationMinutes?: number | null;
  typicalCheckInMinutes?: number | null;
  typicalDriveOffMinutes?: number | null;
  category?: FerryRouteCategory | null;
}

/**
 * PUT `/ferry/api/v1/carrierroutes`. Names and minutes may be set on a synced
 * route as well; the sync never writes them.
 */
export interface UpdateFerryRouteRequest {
  id: string;
  description?: string | null;
  originPortCode?: string | null;
  originPortName?: string | null;
  destinationPortCode?: string | null;
  destinationPortName?: string | null;
  category: FerryRouteCategory;
  accompaniment: FerryAccompaniment;
  typicalDurationMinutes?: number | null;
  typicalCheckInMinutes?: number | null;
  typicalDriveOffMinutes?: number | null;
  isActive: boolean;
}

// ────────────────────────────────────────────────────────────────────────────
// Client
// ────────────────────────────────────────────────────────────────────────────

function withParams(path: string, params: Record<string, string | boolean | undefined>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    qs.set(key, String(value));
  }
  const query = qs.toString();
  return query ? `${path}?${query}` : path;
}

/** Endpoints scoped to the Ferry API (`/ferry/api/v1/*`). Paths are lower case, no hyphens. */
export const ferryApi = {
  getApiInfo: () => request.get<ApiInfo>(`${BASE}/info/api-info`),

  // ── Reference data (Stratum's house login; no credential needed) ────────

  getCarriers: () => request.get<FerryCarrier[]>(`${BASE}/carriers`),

  /** A carrier's routes, or every carrier's when `carrierId` is null. */
  getRoutes: (carrierId: string | null, filter: FerryRouteFilter = {}) =>
    request.get<FerryRoute[]>(
      withParams(`${BASE}/carrierroutes`, {
        carrierId: carrierId ?? undefined,
        category: filter.category,
        isActive: filter.isActive,
      }),
    ),

  getVehicleTypes: (carrierId: string, isActive?: boolean) =>
    request.get<FerryVehicleType[]>(
      withParams(`${BASE}/carriervehicletypes`, { carrierId, isActive }),
    ),

  // ── Catalogue upkeep ────────────────────────────────────────────────────

  createCarrier: (body: CreateFerryCarrierRequest) =>
    request.post<FerryCarrier>(`${BASE}/carriers`, body),

  updateCarrier: (body: UpdateFerryCarrierRequest) =>
    request.put<FerryCarrier>(`${BASE}/carriers`, body),

  createRoute: (body: CreateFerryRouteRequest) =>
    request.post<FerryRoute>(`${BASE}/carrierroutes`, body),

  updateRoute: (body: UpdateFerryRouteRequest) =>
    request.put<FerryRoute>(`${BASE}/carrierroutes`, body),

  /** Sailings on one route between two UTC instants. */
  getSailings: (carrierId: string, routeCode: string, fromUtc: string, toUtc: string) =>
    request.get<FerrySailing[]>(
      withParams(`${BASE}/sailings`, { carrierId, routeCode, from: fromUtc, to: toUtc }),
    ),

  // ── Per organisation ────────────────────────────────────────────────────

  /** The organisation's blocks and bookings on one route, per sailing, with free counts per class. */
  getCapacity: (
    organisationId: string,
    carrierId: string,
    routeCode: string,
    fromUtc: string,
    toUtc: string,
  ) =>
    request.get<FerryCapacity>(
      withParams(`${BASE}/capacityslots`, {
        organisationId,
        carrierId,
        routeCode,
        from: fromUtc,
        to: toUtc,
      }),
    ),

  getCredentials: (organisationId: string, carrierId?: string) =>
    request.get<FerryCredential[]>(
      withParams(`${BASE}/carriercredentials`, { organisationId, carrierId }),
    ),
  createCredential: (body: CreateFerryCredentialRequest) =>
    request.post<FerryCredential>(`${BASE}/carriercredentials`, body),
  updateCredentialStatus: (id: string, status: FerryBaseStatus) =>
    request.put<FerryCredential>(`${BASE}/carriercredentials/status`, { id, status }),
  deleteCredential: (id: string) => request.del<void>(`${BASE}/carriercredentials/${id}`),
  testCredential: (id: string) =>
    request.post<FerryConnectionTest>(`${BASE}/carriercredentials/${id}/test`, {}),

  // ── Bookings ────────────────────────────────────────────────────────────

  getBookings: (organisationId: string, filter: FerryBookingListFilter = {}) =>
    request.get<FerryBooking[]>(withParams(`${BASE}/bookings`, { organisationId, ...filter })),

  getBooking: (id: string) => request.get<FerryBooking>(`${BASE}/bookings/${id}`),

  getBookingEvents: (id: string) =>
    request.get<FerryBookingEvent[]>(`${BASE}/bookings/${id}/events`),

  /**
   * Create at the carrier. `idempotencyKey` is one GUID per attempt — keep it
   * with the attempt and resend the same one on a retry; the service then
   * returns the first answer rather than booking twice.
   *
   * A carrier refusal is NOT an error: the answer is 200 with
   * `integrationStatus: 'Failed'` and the reason in `message`.
   */
  createBooking: (body: CreateFerryBookingRequest, idempotencyKey: string) =>
    request.post<FerryBooking>(`${BASE}/bookings`, body, {
      headers: { 'X-Idempotency-Key': idempotencyKey },
    }),

  /**
   * Amend at the carrier; a new route or departure is a move. The transport
   * mints the idempotency key. A refusal is 200 with `integrationStatus:
   * 'Failed'`, and the booking keeps what the carrier last accepted.
   */
  amendBooking: (id: string, body: AmendFerryBookingRequest) =>
    request.put<FerryBooking>(`${BASE}/bookings/${id}`, body),

  /** Cancel at the carrier. `keepCapacity` asks it to keep the vacated block for the organisation. */
  cancelBooking: (id: string, keepCapacity: boolean) =>
    request.del<FerryBooking>(withParams(`${BASE}/bookings/${id}`, { keepCapacity }), {
      headers: { 'X-Idempotency-Key': crypto.randomUUID() },
    }),

  /** Re-read one booking from the carrier now. */
  refreshBooking: (id: string) => request.post<FerryBooking>(`${BASE}/bookings/${id}/refresh`),
};

export type { ApiInfo };
